import { useRef, useState } from 'react';
import type { MfaMethod } from '@shared/services';
import { type IMfaConfirmRestView, type MfaVerifyResponse } from '@/shared/api/auth';

export type MfaSetupStep = 'select' | 'setup' | 'backup-codes' | 'expired';

/** Why an unfinished ceremony was left: a voluntary Back, or an expired `mfaPending`. */
export type MfaSetupExitReason = 'back' | 'expired';

/**
 * What a completed enrollment hands to its host: TOTP/email `confirm` yield an `IMfaConfirmRestView`
 * (session plus optional `backupCodes`/`accountsProof`), passkey register-verify yields the
 * dual-outcome `MfaVerifyResponse` (session or accounts-proof). The host discriminates — the login
 * wizard captures the proof and continues the flow.
 */
export type MfaSetupResult = MfaVerifyResponse | IMfaConfirmRestView;

/**
 * The enrollment ceremony core state machine (method select → setup → confirm → backup codes). Steps
 * live entirely in memory — no navigation and no history-state reads. It renders as an inline
 * login-wizard step on `/login`; the host decides what completion and exits mean.
 *
 * When the challenge advertises a single method there is nothing to choose, so the machine starts
 * directly on that method's `setup` step and `canReturnToPicker` is false. With two or more methods it
 * starts on `select`.
 *
 * The only deferred completion is TOTP backup codes — they cannot be retrieved later, so they gate
 * `onComplete` behind an explicit acknowledgement.
 */
export const useMfaSetup = (mfaMethods: MfaMethod[], onComplete: (result: MfaSetupResult) => void) => {
  const soleMethod = mfaMethods.length === 1 ? mfaMethods[0] : null;

  const [step, setStep] = useState<MfaSetupStep>(soleMethod ? 'setup' : 'select');
  const [method, setMethod] = useState<MfaMethod | null>(soleMethod);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  // Acknowledging drops the leave guard BEFORE the host continues: an embedded host may finish with a
  // full-page redirect, which an still-active `beforeunload` guard would interrupt with a prompt.
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);
  // The confirm result held while its backup codes await acknowledgement.
  const confirmedRef = useRef<MfaSetupResult | null>(null);

  const canReturnToPicker = mfaMethods.length > 1;

  const selectMethod = (chosen: MfaMethod) => {
    setMethod(chosen);
    setStep('setup');
  };

  const backToPicker = () => {
    setMethod(null);
    setStep('select');
  };

  const expire = () => setStep('expired');

  // TOTP enrollment returns one-time backup codes: hold the confirm result and gate completion on the
  // codes being acknowledged. Email and passkey enrollment return no codes and complete immediately.
  const completeEnrollment = (view: MfaSetupResult) => {
    if ('backupCodes' in view && view.backupCodes && view.backupCodes.length > 0) {
      confirmedRef.current = view;
      setBackupCodes(view.backupCodes);
      setStep('backup-codes');

      return;
    }

    onComplete(view);
  };

  const acknowledgeBackupCodes = () => {
    const confirmed = confirmedRef.current;

    if (!confirmed) {
      return;
    }

    setCodesAcknowledged(true);
    onComplete(confirmed);
  };

  return {
    step,
    method,
    backupCodes,
    codesAcknowledged,
    canReturnToPicker,
    selectMethod,
    backToPicker,
    expire,
    completeEnrollment,
    acknowledgeBackupCodes,
  };
};
